import { createFileRoute } from "@tanstack/react-router";
import {
  ManagementService,
  Material,
  ParseEmployee,
  WorkDayMaterialRequirement,
} from "../../api/generated";
import {
  EmployeeImportTable,
  ImportTableEmployee,
} from "../../components/table/employee_import_table";
import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";

export const Route = createFileRoute("/management/import")({
  component: RouteComponent,
});

type ParseState =
  | { status: "INITIAL" }
  | { status: "LOADING" }
  | {
      employees: ParseEmployee[];
      materials: Material[];
      tableEmployees: Map<string, ImportTableEmployee[]>;
      status: "READY";
    }
  | { error: Error; status: "ERROR" }
  | { status: "IMPORTING" }
  | { status: "IMPORT_COMPLETE" }
  | { status: "DATABASE_EMPTY" }
  | { error: Error; status: "IMPORT_ERROR" };

const maxPerPage = 15;

const mapEmployeesToTableEmployees = (
  employees: ParseEmployee[],
  materials: Material[],
): Map<string, ImportTableEmployee[]> => {
  const tableEmployees = new Map<string, ImportTableEmployee[]>();

  employees.forEach((employee) => {
    employee.workDays.forEach((workDay) => {
      if (!tableEmployees.has(workDay.date)) {
        tableEmployees.set(workDay.date, []);
      }

      const tableEmployee: ImportTableEmployee = {
        firstName: employee.firstName,
        lastName: employee.lastName,
        birthDate: employee.birthDate,
        role: employee.role,
        coordinator: employee.coordinator,
        straps: employee.straps,
        materials: materials
          .map((material) =>
            workDay.materialRequirements.find(
              (m) => m.material.name === material.name,
            ),
          )
          .filter((m): m is WorkDayMaterialRequirement => m !== undefined),
      };

      tableEmployees.get(workDay.date)?.push(tableEmployee);
    });
  });

  return tableEmployees;
};

function RouteComponent() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [state, setState] = useState<ParseState>({ status: "INITIAL" });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileUpload = async () => {
    if (selectedFile) {
      setState({ status: "LOADING" });

      try {
        const result = await ManagementService.postManagementParse({
          file: selectedFile,
        });
        setState({
          status: "READY",
          employees: result.employees,
          materials: result.materials,
          tableEmployees: mapEmployeesToTableEmployees(
            result.employees,
            result.materials,
          ),
        });
      } catch (error) {
        setState({ status: "ERROR", error: error as Error });
      }
    } else {
      setState({
        status: "ERROR",
        error: new Error("Geen bestand geselecteerd"),
      });
    }
  };

  const handleEmployeeImport = async (
    materials: Material[],
    employees: ParseEmployee[],
  ) => {
    setState({ status: "IMPORTING" });

    try {
      await ManagementService.postManagementImport({
        materials: materials,
        employees: employees,
      });
      setState({ status: "IMPORT_COMPLETE" });
    } catch (error) {
      setState({ status: "IMPORT_ERROR", error: error as Error });
    }
  };

  const handleEmptyDatabase = async () => {
    if (
      !confirm(
        "Weet je zeker dat je de database wilt legen? Deze actie kan niet ongedeaan gemaakt worden.",
      )
    ) {
      return;
    }
    setState({ status: "IMPORTING" });
    try {
      await ManagementService.postManagementImport({
        materials: [],
        employees: [],
      });
      setState({ status: "DATABASE_EMPTY" });
    } catch (error) {
      setState({ status: "IMPORT_ERROR", error: error as Error });
    }
  };

  return (
    <div className="flex flex-col justify-center items-center mt-10">
      <div className="flex flex-col w-5/6 items-center justify-center">
        <div role="alert" className="alert alert-warning">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 shrink-0 stroke-current"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span>
            Let op! Het opnieuw importeren van de medewerkers excel zorgt ervoor
            dat alle gegevens worden gereset
          </span>
        </div>

        <div className="card w-full bg-base-200 shadow-xl mt-4">
          <div className="card-body">
            <fieldset className="fieldset w-1/2">
              <input
                type="file"
                className="file-input w-full"
                accept=".xlsx"
                onChange={(event) => handleFileChange(event)}
              />
              <label className="fieldset-label">
                Het moet een Excel (.xlsx) bestand zijn
              </label>
            </fieldset>

            <div className="justify-end card-actions">
              <button
                className="btn btn-error mt-4"
                onClick={() => handleEmptyDatabase()}
              >
                Gegevens opruimen
              </button>
              <button
                className="btn btn-primary mt-4"
                disabled={selectedFile === null}
                onClick={() => handleFileUpload()}
              >
                Inlezen
              </button>
            </div>
          </div>
        </div>

        <div>
          {(state.status === "LOADING" || state.status === "IMPORTING") && (
            <FontAwesomeIcon icon={faSpinner} className="fa-spin mt-4" />
          )}

          {state.status === "ERROR" && (
            <div role="alert" className="alert alert-error w-full mt-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 shrink-0 stroke-current"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>Kon bestand niet uitlezen: {state.error.message}</span>
            </div>
          )}

          {state.status === "READY" && (
            <div className="flex flex-col w-full justify-end">
              <div className="tabs tabs-lift">
                {Array.from(state.tableEmployees.keys()).map((date, index) => (
                  <>
                    <input
                      type="radio"
                      name={`inspoel-tab`}
                      className="tab"
                      aria-label={date}
                      defaultChecked={index === 0}
                    />
                    <div className="tab-content bg-base-100 border-base-300 p-6">
                      <EmployeeImportTable
                        materials={state.materials}
                        employees={state.tableEmployees.get(date) ?? []}
                        itemsPerPage={maxPerPage}
                      />
                    </div>
                  </>
                ))}
              </div>
              <button
                className="btn btn-primary mb-4 mt-4 "
                onClick={() =>
                  handleEmployeeImport(state.materials, state.employees)
                }
              >
                Inspoelen
              </button>
            </div>
          )}

          {state.status === "IMPORT_COMPLETE" && (
            <div role="alert" className="alert alert-info w-full mt-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                className="h-6 w-6 shrink-0 stroke-current"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <span>Medewerkers zijn geïmporteerd</span>
            </div>
          )}

          {state.status === "DATABASE_EMPTY" && (
            <div role="alert" className="alert alert-info w-full mt-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                className="h-6 w-6 shrink-0 stroke-current"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <span>Gegevens zijn verwijdert</span>
            </div>
          )}

          {state.status === "IMPORT_ERROR" && (
            <div role="alert" className="alert alert-error w-full mt-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 shrink-0 stroke-current"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                Kon medewerkers niet importeren: {state.error.message}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
